import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import classNames from "classnames";
import { Squash as Hamburger } from "hamburger-react";
import { motion, AnimatePresence } from "framer-motion";

import { Title } from "@components/ui";
import HeaderLogo from "@components/HeaderLogo/HeaderLogo";
import LangIcon from "@components/Icons/LangIcon";
import AdminLockIcon from "@components/Icons/AdminLockIcon";
import { ContentContainer } from "@components/ContentContainer/ContentContainer";
import { THEME_COLOR } from "@utils/Constants";

import styles from "./Header.module.scss";

export const Header = () => {
    const t = useTranslations("Header");
    let headerColor = "rgba(255, 255, 255, 0.8)";
    const [showHamburgerMenu, setShowHamburgerMenu] = React.useState(false);
    const isFrontPage = useRouter().pathname === "/";
    const { locale, pathname, query } = useRouter();

    return (
        <motion.div
            className={styles.container}
            initial={{
                opacity: isFrontPage ? 0 : 1,
                transform: isFrontPage
                    ? "translate(calc(-50% - 20px), 0px)"
                    : "translate(calc(-50% - 20px), 20px)",
            }}
            animate={{
                opacity: 1,
                transform: "translate(calc(-50% - 20px), 20px)",
            }}
            transition={{ duration: 1 }}
        >
            <header
                className={styles.header}
                style={{ background: headerColor }}
            >
                <ContentContainer className={styles.contentContainer}>
                    <HeaderLogo />

                    <div style={{ flex: 1 }}></div>

                    <nav style={{ height: 65, flexShrink: 0 }}>
                        <div
                            style={{
                                display: "flex",
                            }}
                        >
                            <div
                                className={classNames(
                                    styles.tryFreeButton,
                                    styles.headerAccessories,
                                )}
                            >
                                <ul
                                    style={{
                                        display: "flex",
                                        flexGrow: 1,
                                        height: "100%",
                                    }}
                                >
                                    <HeaderMenuItem
                                        fontColor="#515151"
                                        text={t("companies")}
                                        link="/fyrirtaeki"
                                    />

                                    <HeaderMenuItem
                                        fontColor="#515151"
                                        text={t("municipalities")}
                                        link="/sveitarfelog"
                                    />

                                    <HeaderMenuItem
                                        fontColor="#515151"
                                        text={t("customers")}
                                        link="/vidskiptavinir"
                                    />

                                    <HeaderMenuItem
                                        fontColor="#515151"
                                        text={t("news")}
                                        link="/news"
                                    />

                                    <HeaderMenuItem
                                        fontColor="#515151"
                                        text={t("leaf_school")}
                                        link="/laufaskolinn"
                                    />

                                    <HeaderMenuItem
                                        fontColor="#515151"
                                        text={t("about_us")}
                                        link="/um-okkur"
                                    />

                                    <HeaderMenuItem
                                        className={styles.languageDropdown}
                                        fontColor="#515151"
                                        icon={
                                            <LangIcon
                                                height={35}
                                                width={35}
                                                style={{ marginTop: 15 }}
                                            />
                                        }
                                        dropdownItems={[
                                            {
                                                text: "Íslenska",
                                                link: { pathname, query },
                                                linkLocale: "is",
                                                currentLocale: locale,
                                            },
                                            {
                                                text: "English",
                                                link: { pathname, query },
                                                linkLocale: "en",
                                                currentLocale: locale,
                                            },
                                        ]}
                                    />
                                </ul>
                            </div>
                            <a
                                className={styles.headerAccessories}
                                href="https://admin.laufid.is"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <AdminLockIcon
                                    height={25}
                                    width={25}
                                    style={{ marginLeft: 20 }}
                                />
                            </a>
                            <div className={styles.hamburger}>
                                <Hamburger
                                    toggled={showHamburgerMenu}
                                    color={THEME_COLOR}
                                    rounded
                                    duration={0.5}
                                    onToggle={(value) => {
                                        setShowHamburgerMenu(value);
                                    }}
                                />
                            </div>
                        </div>
                    </nav>
                </ContentContainer>
            </header>
            <AnimatePresence>
                {showHamburgerMenu && (
                    <motion.div
                        exit={{ opacity: 0, transform: "translateY(-10px)" }}
                        initial={{ opacity: 0, transform: "translateY(-10px)" }}
                        animate={{ opacity: 1, transform: "translateY(0px)" }}
                        transition={{ duration: 0.5 }}
                        className={styles.hamburgerMenu}
                        style={{ background: headerColor }}
                    >
                        <ul>
                            <HeaderMenuItem
                                text={t("companies")}
                                link="/fyrirtaeki"
                            />

                            <HeaderMenuItem
                                text={t("municipalities")}
                                link="/sveitarfelog"
                            />

                            <HeaderMenuItem
                                text={t("customers")}
                                link="/vidskiptavinir"
                            />

                            <HeaderMenuItem text={t("news")} link="/news" />
                            <HeaderMenuItem
                                text={t("leaf_school")}
                                link="/laufaskolinn"
                            />

                            <HeaderMenuItem
                                text={t("about_us")}
                                link="/um-okkur"
                            />

                            <HeaderMenuItem
                                text="Íslenska"
                                link={{ pathname, query }}
                                linkLocale="is"
                                currentLocale={locale}
                                icon={
                                    <LangIcon
                                        height={35}
                                        width={35}
                                        style={{ marginTop: 10 }}
                                    />
                                }
                            />

                            <HeaderMenuItem
                                text="English"
                                link={{ pathname, query }}
                                linkLocale="en"
                                currentLocale={locale}
                                icon={
                                    <LangIcon
                                        height={35}
                                        width={35}
                                        style={{ marginTop: 10 }}
                                    />
                                }
                            />

                            <a
                                href="https://admin.laufid.is"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <HeaderMenuItem
                                    text={t("login")}
                                    link="https://admin.laufid.is"
                                    icon={
                                        <AdminLockIcon
                                            height={25}
                                            width={25}
                                            style={{ marginRight: 10 }}
                                        />
                                    }
                                />
                            </a>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

function HeaderMenuItem({
    icon,
    text,
    inverted,
    fontColor,
    className,
    link,
    linkLocale,
    dropdownItems,
}) {
    const router = useRouter();
    const currentRoute = router.pathname;
    let fontWeight = 500;
    if (currentRoute === link) {
        fontColor = THEME_COLOR;
        fontWeight = 600;
    }

    return (
        <li
            className={classNames(
                styles.menuItemText,
                {
                    [styles.inverted]: inverted,
                },
                className,
            )}
        >
            <Link
                href={link ? link : "#"}
                locale={linkLocale}
                style={{
                    cursor: link ? "pointer" : "default",
                    height: "100%",
                }}
            >
                <Title
                    size={5}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        color: fontColor,
                        fontWeight,
                        height: "100%",
                    }}
                >
                    {icon && icon}
                    {text}
                </Title>
            </Link>
            <AnimatePresence>
                {dropdownItems && (
                    <motion.div
                        className={styles.dropdownContainer}
                        exit={{ opacity: 0, transform: "translateY(-10px)" }}
                        initial={{ opacity: 0, transform: "translateY(-10px)" }}
                        animate={{ opacity: 1, transform: "translateY(0px)" }}
                        transition={{ duration: 0.5 }}
                    >
                        <ul className={styles.dropdown}>
                            {dropdownItems.map((item) => {
                                let fontWeight = 500;
                                fontColor = "#515151";
                                if (currentRoute === item.link) {
                                    fontColor = THEME_COLOR;
                                    fontWeight = 600;
                                }

                                if (item.currentLocale === item.linkLocale) {
                                    fontColor = THEME_COLOR;
                                    fontWeight = 600;
                                }
                                return (
                                    <li
                                        className={styles.dropdownItem}
                                        key={item.text}
                                    >
                                        <Link
                                            href={item.link}
                                            locale={item.linkLocale}
                                        >
                                            <Title
                                                size={5}
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    color: fontColor,
                                                    fontWeight,
                                                }}
                                            >
                                                {item.text}
                                            </Title>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </li>
    );
}
